import FirebaseAdapter from "@wc/adapters/firebase";
import { numericToRole } from "@wc/domain/authz";

/**
 * @typedef {object} UserOrgListItem
 * @property {object} org The organization
 * @property {string} org.id The org id
 * @property {string} org.name The URL-safe org name
 * @property {string} org.displayName The display name
 * @property {Date} org.createdAt When the org was created
 * @property {string} role The user's role in this org (string enum)
 * @property {Date} joinedAt When the user joined
 */

/**
 * List all organizations a user is a member of, with their role in each
 * @param {string} userId The user to list orgs for
 * @returns {Promise<UserOrgListItem[]>} Array of orgs with user's role
 */
export async function listUserOrganizations(userId) {
  const memberships = await FirebaseAdapter().membership.listUserOrgs(userId, {
    limit: 100,
  });

  const orgsWithRoles = await Promise.all(
    memberships.map(async (membership) => {
      const [org, roleData] = await Promise.all([
        FirebaseAdapter().organizations.getOrganization(
          membership.organizationId,
        ),
        FirebaseAdapter().roles.getOrgRole(userId, membership.organizationId),
      ]);

      return {
        org: {
          id: org.id,
          name: org.name,
          displayName: org.displayName,
          createdAt: org.createdAt?.toDate
            ? org.createdAt.toDate()
            : org.createdAt,
        },
        role: roleData ? numericToRole(roleData.role) : null,
        joinedAt: membership.joinedAt?.toDate
          ? membership.joinedAt.toDate()
          : membership.joinedAt,
      };
    }),
  );

  return orgsWithRoles;
}
